import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Switch,
  Button,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import {
  useGetPaymentCsvMutation,
  useLazyGetPaymentsQuery,
} from "../../services/main";
import { Loader, showError } from "../../constants";
import { Payments } from "../../types/General";
import Pagination from "../../components/Pagination";
import useAuth from "../../hooks/useAuth";
import useTranslation from "../../hooks/Translation";
import moment from "moment";
import { downloadCsv } from "../../utils/exportCsv";
import SkeletonTable from "../../components/SkeletonTable";
import NoDataFound from "../../components/NoDataFound";
import { formatPhoneLtr } from "../../utils/phoneDisplay";
import {
  PERMISSION_MODULES,
  canShowTableActions,
  getPagePermissionFlags,
} from "../../constants/permissionModules";

const paymentStatusLabel = (status: number | string | undefined, translate: any) => {
  switch (Number(status)) {
    case 1:
      return translate?.payment?.pending || "Pending";
    case 2:
      return translate?.payment?.completed || "Completed";
    case 3:
      return translate?.payment?.cancelled || "Cancelled";
    case 4:
      return translate?.payment?.refunded || "Refunded";
    default:
      return status || "-";
  }
};

const ManagePayments = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [getPaymentQuery, { isLoading }] = useLazyGetPaymentsQuery();
  const [paymentCsv] = useGetPaymentCsvMutation();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [allPayments, setAllPayments] = useState<Payments[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const translate = useTranslation();
  const [hidePermission, setHidePermission] = useState({
    isAdd: true,
    isDelete: true,
    isView: true,
  });
  const [loading, setLoading] = useState(true);

  const checkPermission = () => {
    setHidePermission(
      getPagePermissionFlags(
        userData?.permission,
        PERMISSION_MODULES.MANAGE_PAYMENTS,
        userData?.role === "ADMIN",
      ),
    );
  };

  useEffect(() => {
    if (userData && userData?.permission?.length) {
      checkPermission();
    }
  }, [userData]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };
  let totalPages = Math.ceil(totalCount / 10);

  const getUserCsv = async () => {
    try {
      const response = await paymentCsv({
        type: "user",
        query: debouncedSearchTerm.trim(),
      }).unwrap();
      if (response?.statusCode === 200) {
        await downloadCsv(response?.data, "payments.csv");
      } else {
        showError(translate?.error?.exportFailed || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || translate?.error?.exportFailed || "");
    }
  };

  const getPayment = async () => {
    setLoading(true);
    try {
      const response = await getPaymentQuery({
        query: debouncedSearchTerm.trim(),
        page: page,
      }).unwrap();
      if (response?.statusCode === 200) {
        setLoading(false);
        setAllPayments(response?.data?.payments || []);
        setTotalCount(response?.data?.count);
      } else {
        setLoading(false);
        setAllPayments([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getPayment();
  }, [debouncedSearchTerm, page]);

  return (
    <MainContainer>
      <Loader isLoad={loading} isLinear />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{translate?.main?.ManagePayments}</h1>
        </div>
        <Card className="cards">
          <Box className="cards_header">
            <SearchBar
              value={searchTerm}
              searchTerm={searchTerm}
              onCross={() => setSearchTerm("")}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <Box className="cards_header_right">
              <Button className="btn btn_primary" onClick={getUserCsv}>
                <FileDownloadIcon /> {translate?.dashboard?.ExportCsv}
              </Button>
            </Box>
          </Box>
          <TableContainer className="table_container">
            <Box className="heading"></Box>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{translate?.main?.SNo}</TableCell>

                  <TableCell>{translate?.main?.Name}</TableCell>
                  <TableCell>{translate?.main?.Email}</TableCell>
                  <TableCell>{translate?.main?.PhoneNumber}</TableCell>
                  <TableCell>{translate?.main?.PaymentMode}</TableCell>
                  <TableCell>{translate?.main?.PaymentType}</TableCell>
                  <TableCell>{translate?.payment?.paymentStatus}</TableCell>
                  <TableCell>{translate?.payment?.transactionId}</TableCell>
                  <TableCell>{translate?.payment?.providerChargeId}</TableCell>
                  <TableCell>{translate?.main?.Amount}</TableCell>
                  <TableCell>{translate?.payment?.dateOfPurchase}</TableCell>
                  {canShowTableActions(hidePermission) ? <TableCell>{translate?.main?.Action}</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonTable columns={12} rows={10} />
                ) : allPayments?.length > 0
                  ? allPayments?.map((row, i) => (
                      <TableRow key={row?._id}>
                        <TableCell align="center">
                          {(page - 1) * 10 + i + 1}
                        </TableCell>

                        <TableCell>{row?.user?.fullName || "-"}</TableCell>
                        <TableCell>{row?.user?.email || "-"}</TableCell>
                        <TableCell>
                          {formatPhoneLtr(
                            row?.user?.countryCode,
                            row?.user?.phone
                          )}
                        </TableCell>
                        <TableCell>{row?.paymentMode || "-"}</TableCell>
                        <TableCell>
                          {row?.paymentType === 1
                            ? translate?.payment?.credits
                            : row?.paymentType === 2
                            ? translate?.payment?.subscription
                            : translate?.payment?.agentRequest}
                        </TableCell>
                        <TableCell>
                          {paymentStatusLabel(row?.paymentStatus, translate)}
                        </TableCell>
                        <TableCell>{row?.transactionId || "-"}</TableCell>
                        <TableCell>{row?.providerChargeId || "-"}</TableCell>
                        <TableCell>{row?.amount || "-"}</TableCell>
                        <TableCell>
                          {moment(row?.createdAt).format(
                            "MMM D, YYYY / hh:mm A"
                          )}
                        </TableCell>
                        {canShowTableActions(hidePermission) ? (
                          <TableCell>
                            <Box className="table_actions">
                              <IconButton
                                onClick={() =>
                                  navigate(`/manage-payments/details/${row?._id}`)
                                }
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))
                  : (
                    <TableRow>
                      <TableCell colSpan={12}>
                        <NoDataFound
                          message={
                            translate?.payment?.noPaymentsFound ||
                            "No Payments Found"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {allPayments?.length > 0 ? (
          <Pagination
            setPage={setPage}
            module={allPayments}
            page={page}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        ) : (
          ""
        )}
      </div>
    </MainContainer>
  );
};

export default ManagePayments;
